/**
 * AgenticED Admin API Client (typed implementation).
 *
 * Unified API client for both Vendor Portal and District Admin dashboards.
 * Handles authentication, request formatting, and error handling.
 *
 * Replaces the previous `client.js` + `client.d.ts` shim pair. All ~150
 * domain methods have audited response shapes (per the Domain-by-Domain
 * Type Audit Drain) and use `request<T>` to thread the type through.
 *
 * @see admin/src/api/CONVENTIONS.md
 * @see admin/src/api/responses/
 */

import type {
    DistrictLanguageResponse,
    DistrictLanguageFillListResponse,
    DistrictLanguageFillPassResponse,
    SetDistrictLanguageResponse,
    LanguageCatalogueEntry,
    HomeLanguageDemandResponse,
    FamilyLanguageCountsResponse,
    StartingSetsResponse,
    ApplyStartingSetResponse,
    LanguageMatchResponse,
} from './responses/districtLanguages';
import type {
    BenefitCoverageResponse,
    ProgramServableResponse,
    AdvanceGapResult,
    LocalBenefitRuleListResponse,
    GapStatus,
} from './responses/benefitCoverage';
import type {
    AuditLogsResponse,
} from './responses/audit';
import type {
    TenantResponse,
    UpdateTenantSettingsResponse,
    TenantUsersResponse,
    CreateTenantUserResponse,
    UpdateTenantUserResponse,
    DeleteTenantUserResponse,
    UpdateSchoolBrandingResponse,
    DistrictBrandingResponse,
    UpdateDistrictBrandingResponse,
    SchoolsForBrandingResponse,
} from './responses/tenant';
import type {
    VendorProfileResponse,
    UpdateVendorResponse,
    VendorUsersResponse,
    CreateVendorUserResponse,
    UpdateVendorUserResponse,
    DeleteVendorUserResponse,
} from './responses/vendor';
import type { RegisterVendorResponse } from './responses/vendorAuth';
import type {
    SandboxResponse,
    CreateSandboxResponse,
    ResetSandboxResponse,
    UpdateSandboxWebhookResponse,
    TestSandboxWebhookResponse,
    SandboxLogsResponse,
    RegenerateSandboxApiKeyResponse,
    RegenerateSandboxWebhookSecretResponse,
    VendorBillingResponse,
    VendorPaymentsResponse,
    VendorInvoicesResponse,
    InvoiceDownloadUrlResponse,
    CertificationCheckoutResponse,
    VendorPortalUrlResponse,
    CreateSupportTicketResponse,
    VendorTicketsResponse,
    SupportTicketDetailResponse,
    AddTicketCommentResponse,
} from './responses/sandboxBilling';
import type {
    VendorAgentsResponse,
    RegisterVendorAgentResponse,
    AgentsListResponse,
    AgentDetailResponse,
    RegisterAgentResponse,
    UpdateAgentResponse,
    DeleteAgentResponse,
    SubmitForCertificationResponse,
    CertificationStatusResponse,
    CertificationResultsResponse,
    SubmissionsListResponse,
    SubmissionDetailResponse,
    SubmissionResultsResponse,
} from './responses/vendorAgents';
import type {
    CalendarStatsResponse,
    CalendarAppointmentsResponse,
    CancelAppointmentResponse,
    CalendarStaffResponse,
    StaffAvailabilityResponse,
    SetStaffAvailabilityResponse,
    KnowledgeStatsResponse,
    FafsaEntriesResponse,
    FafsaAmountsResponse,
    UpdateFafsaAmountResponse,
    FafsaDeadlinesResponse,
    UpdateFafsaDeadlineResponse,
    FafsaQueriesResponse,
    ClearFafsaCacheResponse,
    BenefitsAmountsResponse,
    UpdateBenefitsAmountResponse,
    KnowledgeOverviewResponse,
    WorkflowKnowledgeResponse,
    LocalKnowledgeResponse,
    CreateLocalKnowledgeResponse,
    UpdateLocalKnowledgeResponse,
    DeleteLocalKnowledgeResponse,
    BenefitsStatsResponse,
    BenefitsProgramsResponse,
    BenefitsProgramDetailResponse,
    BenefitsFPLResponse,
    BenefitsDiscoveriesResponse,
    BenefitsAgentConfigResponse,
    BenefitsLifeEventsResponse,
} from './responses/calendarKnowledgeBenefits';
import type {
    IntegrationStatusResponse,
    EdFiStatusResponse,
    OneRosterStatusResponse,
    OAuthStatusResponse,
    MCPStatusResponse,
    APIKeysStatusResponse,
    WebhooksStatusResponse,
    TestIntegrationResponse,
    AvailableAgentsResponse,
    EnabledAgentsResponse,
    EnableAgentResponse,
    DisableAgentResponse,
    UpdateAgentConfigResponse,
    TokenBudgetResponse,
    TokenUsageResponse,
    UpdateBudgetAlertsResponse,
    ConsentStatsResponse,
    ConsentsListResponse,
    ConsentAgentsResponse,
    ConsentAgentDetailResponse,
    UpdateAgentTierResponse,
    ConveyancesResponse,
    AcknowledgeConveyanceResponse,
    ExportConveyancesResponse,
    PolicyScopeResponse,
    UpdatePolicyScopeResponse,
    ContextGraphPolicyResponse,
    UpdateContextDomainPolicyResponse,
    UpdateAdultContextPolicyResponse,
    DomainPolicy,
    AdultPolicy,
} from './responses/integrationsConsent';
import type {
    DistrictBillingInvoicesResponse,
    DistrictStripePortalResponse,
    DistrictBillingSummary,
    TenantROIResponse,
    AgentValueResponse,
    EquityImpactResponse,
    BaselinesResponse,
    SetBaselineResponse,
    CostConfigResponse,
    SetCostConfigResponse,
    ValueSummaryResponse,
    MCPToolsResponse,
    MCPAgentsResponse,
    HealthCheckResponse,
    PlatformROIResponse,
    PlatformValueByTenantResponse,
    PlatformValueByAgentResponse,
    AllTenantsResponse,
    TenantDetailsResponse,
    UpdateTenantAdminResponse,
    PlatformRevenueResponse,
    VouchersListResponse,
    CreateVoucherResponse,
    RevokeVoucherResponse,
    SupportTicketsListResponse,
    UpdateSupportTicketResponse,
    AllVendorsResponse,
    SystemHealthResponse,
    FeatureFlagsResponse,
    UpdateFeatureFlagResponse,
    AdminAuditLogsResponse,
    StaffUsersListResponse,
    StaffUserResponse,
    DeleteStaffUserResponse,
} from './responses/valueInternalPlatform';

// ============================================================================
// CONFIGURATION
// ============================================================================

// API is served from the same domain via path routing (/api/*).
// Use relative URLs - VITE_API_URL can override for development.
const API_BASE: string = import.meta.env.VITE_API_URL || '';

export type AuthType = 'api_key' | 'session' | 'none';

export const AuthType = {
    API_KEY: 'api_key' as const,
    SESSION: 'session' as const,
    NONE: 'none' as const,
} as const;

type Params = Record<string, string | number | boolean | undefined>;
type JsonBody = Record<string, unknown>;

function buildQuery(params: Params | undefined): string {
    if (!params) return '';
    const filtered: Record<string, string> = {};
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '') {
            filtered[k] = String(v);
        }
    }
    return new URLSearchParams(filtered).toString();
}

// ============================================================================
// API ERROR CLASS
// ============================================================================

export class APIError extends Error {
    readonly name = 'APIError';
    readonly status: number;
    readonly code: string;
    readonly data: unknown;

    constructor(message: string, status: number, code: string, data: unknown) {
        super(message);
        this.status = status;
        this.code = code;
        this.data = data;
    }

    isAuthError(): boolean {
        return this.status === 401 || this.status === 403;
    }

    isValidationError(): boolean {
        return this.status === 400 || this.status === 422;
    }

    isServerError(): boolean {
        return this.status >= 500;
    }
}

// ============================================================================
// API CLIENT CLASS
// ============================================================================

export class APIClient {
    /**
     * Public class fields. Read directly by one .jsx consumer
     * (RegistryDataManagement.jsx:81-86). Migrate that consumer before
     * making these private.
     */
    apiKey: string | null = null;
    sessionToken: string | null = null;
    authType: AuthType = AuthType.NONE;

    constructor() {
        this._loadStoredCredentials();
    }

    /** @internal — re-reads localStorage on every request. */
    _loadStoredCredentials(): void {
        const storedApiKey = localStorage.getItem('agenticed_api_key');
        const storedSession = localStorage.getItem('agenticed_session');

        if (storedApiKey) {
            this.apiKey = storedApiKey;
            this.authType = AuthType.API_KEY;
        } else if (storedSession) {
            this.sessionToken = storedSession;
            this.authType = AuthType.SESSION;
        } else {
            // Fallback: read token from authService session (agenticed_admin_auth)
            try {
                const authSession = localStorage.getItem('agenticed_admin_auth');
                if (authSession) {
                    const parsed = JSON.parse(authSession) as { token?: string };
                    if (parsed.token) {
                        this.sessionToken = parsed.token;
                        this.authType = AuthType.SESSION;
                    }
                }
            } catch { /* ignore parse errors */ }
        }
    }

    setApiKey(key: string): void {
        this.apiKey = key;
        this.authType = AuthType.API_KEY;
        localStorage.setItem('agenticed_api_key', key);
        localStorage.removeItem('agenticed_session');
    }

    setSessionToken(token: string): void {
        this.sessionToken = token;
        this.authType = AuthType.SESSION;
        localStorage.setItem('agenticed_session', token);
        localStorage.removeItem('agenticed_api_key');
    }

    /**
     * Public alias for _getAuthHeaders. Intended for callers that bypass
     * the apiClient.request wrapper (e.g. binary blob downloads via
     * native fetch).
     */
    getAuthHeaders(): Record<string, string> {
        return this._getAuthHeaders();
    }

    clearAuth(): void {
        this.apiKey = null;
        this.sessionToken = null;
        this.authType = AuthType.NONE;
        localStorage.removeItem('agenticed_api_key');
        localStorage.removeItem('agenticed_session');
    }

    isAuthenticated(): boolean {
        return this.authType !== AuthType.NONE;
    }

    getAuthType(): AuthType {
        return this.authType;
    }

    /**
     * Generic JSON request. Defaults T to unknown so consumers without
     * a typed shape don't fall back to implicit any.
     *
     * Non-JSON 200 responses (rare — used by raw text endpoints) return
     * `string` cast as T. Non-ok responses throw APIError.
     */
    async request<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> {
        // Re-read credentials from localStorage before each request to pick
        // up token refreshes or logins from other components.
        this._loadStoredCredentials();

        const url = `${API_BASE}${endpoint}`;
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...(options.headers as Record<string, string> | undefined),
        };

        if (this.authType === AuthType.API_KEY && this.apiKey) {
            headers['X-Api-Key'] = this.apiKey;
        } else if (this.authType === AuthType.SESSION && this.sessionToken) {
            headers['Authorization'] = `Bearer ${this.sessionToken}`;
        }

        try {
            const response = await fetch(url, {
                ...options,
                headers,
                credentials: 'include',
            });

            const contentType = response.headers.get('content-type') || '';
            const isJson = contentType.includes('application/json');

            if (!response.ok) {
                // Two error-envelope shapes reach this client: the flat admin shape
                // ({ message } or { error: "<string>" }) and the GTA shape
                // ({ error: { code, message } }, a nested object). Reading
                // `errorData.error` blindly and using it as the Error message turned the
                // GTA object into the literal string "[object Object]" — the bug the
                // district surfaces showed. Normalize BOTH shapes to a real string +
                // code so a nested envelope is never stringified.
                let errorData: {
                    message?: string;
                    error?: string | { code?: string; message?: string };
                    code?: string;
                } = {};
                if (isJson) {
                    try { errorData = await response.json(); } catch { /* ignore */ }
                } else {
                    const text = await response.text().catch(() => '');
                    errorData = { message: `Server error (${response.status})${text ? ' — check server logs' : ''}` };
                }
                const nested = errorData.error && typeof errorData.error === 'object' ? errorData.error : null;
                const flatError = typeof errorData.error === 'string' ? errorData.error : undefined;
                const message = errorData.message || (nested && nested.message) || flatError || `Request failed (${response.status})`;
                const code = errorData.code || (nested && nested.code) || 'UNKNOWN_ERROR';
                throw new APIError(message, response.status, code, errorData);
            }

            const data = isJson ? await response.json() : await response.text();
            return data as T;
        } catch (error) {
            if (error instanceof APIError) {
                throw error;
            }
            throw new APIError(
                error instanceof Error ? error.message : 'Network error',
                0,
                'NETWORK_ERROR',
                null,
            );
        }
    }

    /**
     * Raw fetch passthrough for binary downloads (PDFs etc.).
     */
    async requestRaw(endpoint: string, options: RequestInit = {}): Promise<Response> {
        this._loadStoredCredentials();

        const url = `${API_BASE}${endpoint}`;
        const headers: Record<string, string> = { ...(options.headers as Record<string, string> | undefined) };

        if (this.authType === AuthType.API_KEY && this.apiKey) {
            headers['X-Api-Key'] = this.apiKey;
        } else if (this.authType === AuthType.SESSION && this.sessionToken) {
            headers['Authorization'] = `Bearer ${this.sessionToken}`;
        }

        const response = await fetch(url, { ...options, headers, credentials: 'include' });

        if (!response.ok) {
            let message = `Request failed (${response.status})`;
            try {
                const contentType = response.headers.get('content-type') || '';
                if (contentType.includes('application/json')) {
                    const body = await response.json() as { message?: string; error?: string };
                    message = body.message || body.error || message;
                }
            } catch { /* ignore parse errors */ }
            throw new APIError(message, response.status, 'REQUEST_FAILED', null);
        }

        return response;
    }

    /**
     * POST that prepends `/api/sdk` to the endpoint.
     * @deprecated The `/api/sdk` auto-prefix is a footgun; prefer `request()`
     * with an explicit path.
     */
    async post<T = unknown>(endpoint: string, body: JsonBody = {}): Promise<T> {
        return this.request<T>(`/api/sdk${endpoint}`, {
            method: 'POST',
            body: JSON.stringify(body),
        });
    }

    /** @internal — used by exportComplianceReport. Public alias: getAuthHeaders. */
    _getAuthHeaders(): Record<string, string> {
        const headers: Record<string, string> = {};
        if (this.authType === AuthType.API_KEY && this.apiKey) {
            headers['X-Api-Key'] = this.apiKey;
        } else if (this.authType === AuthType.SESSION && this.sessionToken) {
            headers['Authorization'] = `Bearer ${this.sessionToken}`;
        }
        return headers;
    }

    // ========================================================================
    // VENDOR PROFILE + USERS
    // ========================================================================

    registerVendor(data: {
        company_name: string;
        contact_name?: string;
        contact_email: string;
        password?: string;
        website?: string;
        description?: string;
    }): Promise<RegisterVendorResponse> {
        return this.request<RegisterVendorResponse>('/api/sdk/vendors/register', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    getVendor(): Promise<VendorProfileResponse> {
        return this.request<VendorProfileResponse>('/api/sdk/vendors/me');
    }

    updateVendor(data: JsonBody): Promise<UpdateVendorResponse> {
        return this.request<UpdateVendorResponse>('/api/sdk/vendors/me', {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    listVendorUsers(params: Params = {}): Promise<VendorUsersResponse> {
        const query = buildQuery(params);
        return this.request<VendorUsersResponse>(query ? `/api/sdk/vendors/me/users?${query}` : '/api/sdk/vendors/me/users');
    }

    createVendorUser(data: JsonBody): Promise<CreateVendorUserResponse> {
        return this.request<CreateVendorUserResponse>('/api/sdk/vendors/me/users', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    updateVendorUser(userId: string, data: JsonBody): Promise<UpdateVendorUserResponse> {
        return this.request<UpdateVendorUserResponse>(`/api/sdk/vendors/me/users/${userId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    deleteVendorUser(userId: string): Promise<DeleteVendorUserResponse> {
        return this.request<DeleteVendorUserResponse>(`/api/sdk/vendors/me/users/${userId}`, {
            method: 'DELETE',
        });
    }

    // ========================================================================
    // VENDOR AGENTS + CERTIFICATION
    // ========================================================================

    listVendorAgents(): Promise<VendorAgentsResponse> {
        return this.request<VendorAgentsResponse>('/api/sdk/vendors/me/agents');
    }

    registerVendorAgent(data: JsonBody): Promise<RegisterVendorAgentResponse> {
        return this.request<RegisterVendorAgentResponse>('/api/sdk/vendors/me/agents', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    listAgents(params: Params = {}): Promise<AgentsListResponse> {
        const query = buildQuery(params);
        return this.request<AgentsListResponse>(query ? `/api/sdk/agents?${query}` : '/api/sdk/agents');
    }

    getAgent(agentId: string): Promise<AgentDetailResponse> {
        return this.request<AgentDetailResponse>(`/api/sdk/agents/${agentId}`);
    }

    registerAgent(data: JsonBody): Promise<RegisterAgentResponse> {
        return this.request<RegisterAgentResponse>('/api/sdk/agents', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    updateAgent(agentId: string, data: JsonBody): Promise<UpdateAgentResponse> {
        return this.request<UpdateAgentResponse>(`/api/sdk/agents/${agentId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    deleteAgent(agentId: string): Promise<DeleteAgentResponse> {
        return this.request<DeleteAgentResponse>(`/api/sdk/agents/${agentId}`, {
            method: 'DELETE',
        });
    }

    submitForCertification(agentId: string): Promise<SubmitForCertificationResponse> {
        return this.request<SubmitForCertificationResponse>(`/api/sdk/agents/${agentId}/submit`, {
            method: 'POST',
        });
    }

    getCertificationStatus(agentId: string): Promise<CertificationStatusResponse> {
        return this.request<CertificationStatusResponse>(`/api/sdk/agents/${agentId}/certification`);
    }

    getCertificationResults(agentId: string): Promise<CertificationResultsResponse> {
        return this.request<CertificationResultsResponse>(`/api/sdk/agents/${agentId}/certification/results`);
    }

    listSubmissions(params: Params = {}): Promise<SubmissionsListResponse> {
        const query = buildQuery(params);
        return this.request<SubmissionsListResponse>(query ? `/api/sdk/vendors/me/submissions?${query}` : '/api/sdk/vendors/me/submissions');
    }

    getSubmission(submissionId: string): Promise<SubmissionDetailResponse> {
        return this.request<SubmissionDetailResponse>(`/api/sdk/submissions/${submissionId}`);
    }

    getSubmissionResults(submissionId: string): Promise<SubmissionResultsResponse> {
        return this.request<SubmissionResultsResponse>(`/api/sdk/submissions/${submissionId}/results`);
    }

    // ========================================================================
    // SANDBOX
    // ========================================================================

    getSandbox(): Promise<SandboxResponse> {
        return this.request<SandboxResponse>('/api/sdk/sandbox');
    }

    createSandbox(): Promise<CreateSandboxResponse> {
        return this.request<CreateSandboxResponse>('/api/sdk/sandbox', { method: 'POST' });
    }

    resetSandbox(): Promise<ResetSandboxResponse> {
        return this.request<ResetSandboxResponse>('/api/sdk/sandbox/reset', { method: 'POST' });
    }

    updateSandboxWebhook(webhookUrl: string): Promise<UpdateSandboxWebhookResponse> {
        return this.request<UpdateSandboxWebhookResponse>('/api/sdk/sandbox/webhook', {
            method: 'PUT',
            body: JSON.stringify({ webhook_url: webhookUrl }),
        });
    }

    testSandboxWebhook(eventType: string): Promise<TestSandboxWebhookResponse> {
        return this.request<TestSandboxWebhookResponse>('/api/sdk/sandbox/webhook/test', {
            method: 'POST',
            body: JSON.stringify({ event_type: eventType }),
        });
    }

    getSandboxLogs(params: Params = {}): Promise<SandboxLogsResponse> {
        const query = buildQuery(params);
        return this.request<SandboxLogsResponse>(query ? `/api/sdk/sandbox/logs?${query}` : '/api/sdk/sandbox/logs');
    }

    regenerateSandboxApiKey(): Promise<RegenerateSandboxApiKeyResponse> {
        return this.request<RegenerateSandboxApiKeyResponse>('/api/sdk/sandbox/keys/regenerate', { method: 'POST' });
    }

    regenerateSandboxWebhookSecret(): Promise<RegenerateSandboxWebhookSecretResponse> {
        return this.request<RegenerateSandboxWebhookSecretResponse>('/api/sdk/sandbox/webhook-secret/regenerate', { method: 'POST' });
    }

    // ========================================================================
    // VENDOR BILLING
    // ========================================================================

    getVendorBilling(): Promise<VendorBillingResponse> {
        return this.request<VendorBillingResponse>('/api/payments/vendors/billing');
    }

    getVendorPayments(params: Params = {}): Promise<VendorPaymentsResponse> {
        const query = buildQuery(params);
        return this.request<VendorPaymentsResponse>(query ? `/api/payments/vendors/payments?${query}` : '/api/payments/vendors/payments');
    }

    getVendorInvoices(params: Params = {}): Promise<VendorInvoicesResponse> {
        const query = buildQuery(params);
        return this.request<VendorInvoicesResponse>(query ? `/api/payments/vendors/invoices?${query}` : '/api/payments/vendors/invoices');
    }

    getInvoiceDownloadUrl(invoiceId: string): Promise<InvoiceDownloadUrlResponse> {
        return this.request<InvoiceDownloadUrlResponse>(`/api/payments/vendors/invoices/${invoiceId}/download`);
    }

    createCertificationCheckout(params: { fee_type?: 'sandbox' | 'certification' } = {}): Promise<CertificationCheckoutResponse> {
        return this.request<CertificationCheckoutResponse>('/api/payments/vendors/certification/checkout', {
            method: 'POST',
            body: JSON.stringify(params),
        });
    }

    getVendorPortalUrl(): Promise<VendorPortalUrlResponse> {
        return this.request<VendorPortalUrlResponse>('/api/payments/vendors/portal', { method: 'POST' });
    }

    // ========================================================================
    // SUPPORT TICKETS
    // ========================================================================

    createSupportTicket(data: {
        subject: string;
        category: string;
        priority?: string;
        message: string;
    }): Promise<CreateSupportTicketResponse> {
        return this.request<CreateSupportTicketResponse>('/api/escalations', {
            method: 'POST',
            body: JSON.stringify({
                type: 'vendor_support',
                priority: data.priority || 'normal',
                subject: data.subject,
                category: data.category,
                description: data.message,
            }),
        });
    }

    getVendorTickets(params: Params = {}): Promise<VendorTicketsResponse> {
        const query = buildQuery(params);
        return this.request<VendorTicketsResponse>(query ? `/api/escalations?${query}` : '/api/escalations');
    }

    getTicket(ticketId: string): Promise<SupportTicketDetailResponse> {
        return this.request<SupportTicketDetailResponse>(`/api/escalations/${ticketId}`);
    }

    addTicketComment(ticketId: string, message: string): Promise<AddTicketCommentResponse> {
        return this.request<AddTicketCommentResponse>(`/api/escalations/${ticketId}/comments`, {
            method: 'POST',
            body: JSON.stringify({ message }),
        });
    }

    // ========================================================================
    // DISTRICT TENANT + BRANDING
    // ========================================================================

    getTenant(): Promise<TenantResponse> {
        return this.request<TenantResponse>('/api/admin/tenant');
    }

    updateTenantSettings(settings: JsonBody): Promise<UpdateTenantSettingsResponse> {
        return this.request<UpdateTenantSettingsResponse>('/api/admin/tenant/settings', {
            method: 'PUT',
            body: JSON.stringify(settings),
        });
    }

    listTenantUsers(params: Params = {}): Promise<TenantUsersResponse> {
        const query = buildQuery(params);
        return this.request<TenantUsersResponse>(query ? `/api/admin/users?${query}` : '/api/admin/users');
    }

    createTenantUser(data: JsonBody): Promise<CreateTenantUserResponse> {
        return this.request<CreateTenantUserResponse>('/api/admin/users', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    updateTenantUser(userId: string, data: JsonBody): Promise<UpdateTenantUserResponse> {
        return this.request<UpdateTenantUserResponse>(`/api/admin/users/${userId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    deleteTenantUser(userId: string): Promise<DeleteTenantUserResponse> {
        return this.request<DeleteTenantUserResponse>(`/api/admin/users/${userId}`, {
            method: 'DELETE',
        });
    }

    updateSchoolBranding(schoolId: string, branding: JsonBody): Promise<UpdateSchoolBrandingResponse> {
        return this.request<UpdateSchoolBrandingResponse>(`/api/admin/schools/${schoolId}/branding`, {
            method: 'PUT',
            body: JSON.stringify(branding),
        });
    }

    getDistrictBranding(): Promise<DistrictBrandingResponse> {
        return this.request<DistrictBrandingResponse>('/api/admin/branding');
    }

    updateDistrictBranding(branding: JsonBody): Promise<UpdateDistrictBrandingResponse> {
        return this.request<UpdateDistrictBrandingResponse>('/api/admin/branding', {
            method: 'PUT',
            body: JSON.stringify(branding),
        });
    }

    getSchoolsForBranding(): Promise<SchoolsForBrandingResponse> {
        return this.request<SchoolsForBrandingResponse>('/api/admin/schools/branding');
    }

    // ========================================================================
    // CALENDAR MANAGEMENT (DISTRICT)
    // ========================================================================

    getCalendarStats(): Promise<CalendarStatsResponse> {
        return this.request<CalendarStatsResponse>('/api/admin/calendar/stats');
    }

    listAppointments(params: Params = {}): Promise<CalendarAppointmentsResponse> {
        const query = buildQuery(params);
        return this.request<CalendarAppointmentsResponse>(query ? `/api/admin/calendar/appointments?${query}` : '/api/admin/calendar/appointments');
    }

    cancelAppointment(appointmentId: string, reason: string): Promise<CancelAppointmentResponse> {
        return this.request<CancelAppointmentResponse>(`/api/admin/calendar/appointments/${appointmentId}`, {
            method: 'DELETE',
            body: JSON.stringify({ reason }),
        });
    }

    listCalendarStaff(): Promise<CalendarStaffResponse> {
        return this.request<CalendarStaffResponse>('/api/admin/calendar/staff');
    }

    getStaffAvailability(userId: string): Promise<StaffAvailabilityResponse> {
        return this.request<StaffAvailabilityResponse>(`/api/admin/calendar/availability/${userId}`);
    }

    setStaffAvailability(userId: string, windows: unknown[]): Promise<SetStaffAvailabilityResponse> {
        return this.request<SetStaffAvailabilityResponse>(`/api/admin/calendar/availability/${userId}`, {
            method: 'POST',
            body: JSON.stringify({ windows }),
        });
    }

    // ========================================================================
    // KNOWLEDGE BASE MANAGEMENT (DISTRICT)
    // ========================================================================

    getKnowledgeStats(): Promise<KnowledgeStatsResponse> {
        return this.request<KnowledgeStatsResponse>('/api/admin/knowledge/stats');
    }

    listFAFSAEntries(params: Params = {}): Promise<FafsaEntriesResponse> {
        const query = buildQuery(params);
        return this.request<FafsaEntriesResponse>(query ? `/api/admin/knowledge/fafsa/entries?${query}` : '/api/admin/knowledge/fafsa/entries');
    }

    listFAFSAAmounts(): Promise<FafsaAmountsResponse> {
        return this.request<FafsaAmountsResponse>('/api/admin/knowledge/fafsa/amounts');
    }

    updateFAFSAAmount(id: string, data: JsonBody): Promise<UpdateFafsaAmountResponse> {
        return this.request<UpdateFafsaAmountResponse>(`/api/admin/knowledge/fafsa/amounts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    listFAFSADeadlines(): Promise<FafsaDeadlinesResponse> {
        return this.request<FafsaDeadlinesResponse>('/api/admin/knowledge/fafsa/deadlines');
    }

    updateFAFSADeadline(id: string, data: JsonBody): Promise<UpdateFafsaDeadlineResponse> {
        return this.request<UpdateFafsaDeadlineResponse>(`/api/admin/knowledge/fafsa/deadlines/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    listFAFSAQueries(params: Params = {}): Promise<FafsaQueriesResponse> {
        const query = buildQuery(params);
        return this.request<FafsaQueriesResponse>(query ? `/api/admin/knowledge/fafsa/queries?${query}` : '/api/admin/knowledge/fafsa/queries');
    }

    clearFAFSACache(): Promise<ClearFafsaCacheResponse> {
        return this.request<ClearFafsaCacheResponse>('/api/admin/knowledge/fafsa/cache/clear', { method: 'POST' });
    }

    listBenefitsAmounts(): Promise<BenefitsAmountsResponse> {
        return this.request<BenefitsAmountsResponse>('/api/admin/knowledge/benefits/amounts');
    }

    updateBenefitsAmount(id: string, data: JsonBody): Promise<UpdateBenefitsAmountResponse> {
        return this.request<UpdateBenefitsAmountResponse>(`/api/admin/knowledge/benefits/amounts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    // ========================================================================
    // KNOWLEDGE BASE MANAGEMENT (UNIFIED)
    // ========================================================================

    getKnowledgeOverview(): Promise<KnowledgeOverviewResponse> {
        return this.request<KnowledgeOverviewResponse>('/api/admin/knowledge/overview');
    }

    getWorkflowKnowledge(params: Params = {}): Promise<WorkflowKnowledgeResponse> {
        const query = buildQuery(params);
        return this.request<WorkflowKnowledgeResponse>(`/api/admin/knowledge/workflow${query ? '?' + query : ''}`);
    }

    getLocalKnowledge(params: Params = {}): Promise<LocalKnowledgeResponse> {
        const query = buildQuery(params);
        return this.request<LocalKnowledgeResponse>(`/api/admin/knowledge/local${query ? '?' + query : ''}`);
    }

    createLocalKnowledge(data: JsonBody): Promise<CreateLocalKnowledgeResponse> {
        return this.request<CreateLocalKnowledgeResponse>('/api/admin/knowledge/local', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    updateLocalKnowledge(id: string, data: JsonBody): Promise<UpdateLocalKnowledgeResponse> {
        return this.request<UpdateLocalKnowledgeResponse>(`/api/admin/knowledge/local/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    deleteLocalKnowledge(id: string): Promise<DeleteLocalKnowledgeResponse> {
        return this.request<DeleteLocalKnowledgeResponse>(`/api/admin/knowledge/local/${id}`, {
            method: 'DELETE',
        });
    }

    // ========================================================================
    // BENEFITS ADMIN
    // ========================================================================

    getBenefitsStats(): Promise<BenefitsStatsResponse> {
        return this.request<BenefitsStatsResponse>('/api/admin/benefits/stats');
    }

    listBenefitsPrograms(): Promise<BenefitsProgramsResponse> {
        return this.request<BenefitsProgramsResponse>('/api/admin/benefits/programs');
    }

    getBenefitsProgram(id: string): Promise<BenefitsProgramDetailResponse> {
        return this.request<BenefitsProgramDetailResponse>(`/api/admin/benefits/programs/${id}`);
    }

    getBenefitsFPL(): Promise<BenefitsFPLResponse> {
        return this.request<BenefitsFPLResponse>('/api/admin/benefits/fpl');
    }

    listBenefitsDiscoveries(params: Params = {}): Promise<BenefitsDiscoveriesResponse> {
        const query = buildQuery(params);
        return this.request<BenefitsDiscoveriesResponse>(query ? `/api/admin/benefits/discoveries?${query}` : '/api/admin/benefits/discoveries');
    }

    getBenefitsAgentConfig(): Promise<BenefitsAgentConfigResponse> {
        return this.request<BenefitsAgentConfigResponse>('/api/admin/benefits/agent-config');
    }

    updateBenefitsAgentConfig(config: JsonBody): Promise<BenefitsAgentConfigResponse> {
        return this.request<BenefitsAgentConfigResponse>('/api/admin/benefits/agent-config', {
            method: 'PUT',
            body: JSON.stringify(config),
        });
    }

    getBenefitsLifeEvents(): Promise<BenefitsLifeEventsResponse> {
        return this.request<BenefitsLifeEventsResponse>('/api/admin/benefits/life-events');
    }

    // ========================================================================
    // INTEGRATION MONITORING
    // ========================================================================

    getIntegrationStatus(): Promise<IntegrationStatusResponse> {
        return this.request<IntegrationStatusResponse>('/api/admin/integrations/status');
    }

    getEdFiStatus(): Promise<EdFiStatusResponse> {
        return this.request<EdFiStatusResponse>('/api/admin/integrations/edfi');
    }

    getOneRosterStatus(): Promise<OneRosterStatusResponse> {
        return this.request<OneRosterStatusResponse>('/api/admin/integrations/oneroster');
    }

    getOAuthStatus(): Promise<OAuthStatusResponse> {
        return this.request<OAuthStatusResponse>('/api/admin/integrations/oauth');
    }

    getMCPStatus(): Promise<MCPStatusResponse> {
        return this.request<MCPStatusResponse>('/api/admin/integrations/mcp');
    }

    getAPIKeysStatus(): Promise<APIKeysStatusResponse> {
        return this.request<APIKeysStatusResponse>('/api/admin/integrations/api-keys');
    }

    getWebhooksStatus(): Promise<WebhooksStatusResponse> {
        return this.request<WebhooksStatusResponse>('/api/admin/integrations/webhooks');
    }

    testIntegration(integration: string): Promise<TestIntegrationResponse> {
        return this.request<TestIntegrationResponse>(`/api/admin/integrations/test/${integration}`, { method: 'POST' });
    }

    // ========================================================================
    // AGENT MANAGEMENT (DISTRICT)
    // ========================================================================

    listAvailableAgents(params: Params = {}): Promise<AvailableAgentsResponse> {
        const query = buildQuery(params);
        return this.request<AvailableAgentsResponse>(query ? `/api/admin/agents/available?${query}` : '/api/admin/agents/available');
    }

    listEnabledAgents(): Promise<EnabledAgentsResponse> {
        return this.request<EnabledAgentsResponse>('/api/admin/agents/enabled');
    }

    enableAgent(serverId: string, config: JsonBody = {}): Promise<EnableAgentResponse> {
        return this.request<EnableAgentResponse>(`/api/admin/agents/${serverId}/enable`, {
            method: 'POST',
            body: JSON.stringify(config),
        });
    }

    disableAgent(serverId: string): Promise<DisableAgentResponse> {
        return this.request<DisableAgentResponse>(`/api/admin/agents/${serverId}/disable`, { method: 'POST' });
    }

    updateAgentConfig(serverId: string, config: JsonBody): Promise<UpdateAgentConfigResponse> {
        return this.request<UpdateAgentConfigResponse>(`/api/admin/agents/${serverId}/config`, {
            method: 'PUT',
            body: JSON.stringify(config),
        });
    }

    // ========================================================================
    // TOKEN BUDGET
    // ========================================================================

    getTokenBudget(): Promise<TokenBudgetResponse> {
        return this.request<TokenBudgetResponse>('/api/admin/budget');
    }

    getTokenUsage(params: Params = {}): Promise<TokenUsageResponse> {
        const query = buildQuery(params);
        return this.request<TokenUsageResponse>(query ? `/api/admin/budget/usage?${query}` : '/api/admin/budget/usage');
    }

    updateBudgetAlerts(alerts: JsonBody): Promise<UpdateBudgetAlertsResponse> {
        return this.request<UpdateBudgetAlertsResponse>('/api/admin/budget/alerts', {
            method: 'PUT',
            body: JSON.stringify(alerts),
        });
    }

    // ========================================================================
    // CONSENT MANAGEMENT (DISTRICT)
    // ========================================================================

    getConsentStats(): Promise<ConsentStatsResponse> {
        return this.request<ConsentStatsResponse>('/api/admin/consent/stats');
    }

    listConsents(params: Params = {}): Promise<ConsentsListResponse> {
        const query = buildQuery(params);
        return this.request<ConsentsListResponse>(query ? `/api/admin/consent?${query}` : '/api/admin/consent');
    }

    listConsentAgents(): Promise<ConsentAgentsResponse> {
        return this.request<ConsentAgentsResponse>('/api/admin/consent/agents');
    }

    getConsentAgentDetail(agentId: string): Promise<ConsentAgentDetailResponse> {
        return this.request<ConsentAgentDetailResponse>(`/api/admin/consent/agents/${agentId}`);
    }

    updateAgentTier(agentId: string, data: JsonBody): Promise<UpdateAgentTierResponse> {
        return this.request<UpdateAgentTierResponse>(`/api/admin/consent/agents/${agentId}/tier`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    listConveyances(params: Params = {}): Promise<ConveyancesResponse> {
        const query = buildQuery(params);
        return this.request<ConveyancesResponse>(query ? `/api/admin/consent/conveyance?${query}` : '/api/admin/consent/conveyance');
    }

    acknowledgeConveyance(conveyanceId: string): Promise<AcknowledgeConveyanceResponse> {
        return this.request<AcknowledgeConveyanceResponse>(`/api/admin/consent/conveyance/${conveyanceId}/acknowledge`, { method: 'POST' });
    }

    exportConveyances(): Promise<ExportConveyancesResponse> {
        return this.request<ExportConveyancesResponse>('/api/admin/consent/conveyance/export');
    }

    getPolicyScope(): Promise<PolicyScopeResponse> {
        return this.request<PolicyScopeResponse>('/api/admin/consent/policy-scope');
    }

    updatePolicyScope(data: JsonBody): Promise<UpdatePolicyScopeResponse> {
        return this.request<UpdatePolicyScopeResponse>('/api/admin/consent/policy-scope', {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    getContextGraphPolicy(): Promise<ContextGraphPolicyResponse> {
        return this.request<ContextGraphPolicyResponse>('/api/admin/context/graph-policy');
    }

    updateContextDomainPolicy(domainId: string, data: Partial<DomainPolicy>): Promise<UpdateContextDomainPolicyResponse> {
        return this.request<UpdateContextDomainPolicyResponse>(`/api/admin/context/graph-policy/${domainId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    updateAdultContextPolicy(data: Partial<AdultPolicy>): Promise<UpdateAdultContextPolicyResponse> {
        return this.request<UpdateAdultContextPolicyResponse>('/api/admin/context/adult-policy', {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    // ========================================================================
    // AUDIT & COMPLIANCE
    // ========================================================================

    getAuditLogs(params: Params = {}): Promise<AuditLogsResponse> {
        const query = buildQuery(params);
        return this.request<AuditLogsResponse>(query ? `/api/admin/audit?${query}` : '/api/admin/audit');
    }

    async exportComplianceReport(format: 'pdf' | 'csv' | 'json' = 'pdf'): Promise<Blob> {
        const response = await fetch(`${API_BASE}/api/admin/compliance/export?format=${format}`, {
            headers: this._getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new APIError('Export failed', response.status, 'EXPORT_ERROR', null);
        }

        return response.blob();
    }

    // ========================================================================
    // DISTRICT BILLING
    // ========================================================================

    getBillingInvoices(tenantId: string): Promise<DistrictBillingInvoicesResponse> {
        return this.request<DistrictBillingInvoicesResponse>(`/api/payments/districts/${tenantId}/invoices`);
    }

    getStripePortalUrl(tenantId: string): Promise<DistrictStripePortalResponse> {
        return this.request<DistrictStripePortalResponse>(`/api/payments/districts/${tenantId}/portal`, { method: 'POST' });
    }

    getBillingSummary(tenantId: string): Promise<DistrictBillingSummary> {
        return this.request<DistrictBillingSummary>(`/api/payments/districts/${tenantId}/summary`);
    }

    // ========================================================================
    // VALUE INTELLIGENCE
    // ========================================================================

    getTenantROI(tenantId: string, params: Params = {}): Promise<TenantROIResponse> {
        const query = buildQuery(params);
        return this.request<TenantROIResponse>(query ? `/api/value/tenant/${tenantId}/roi?${query}` : `/api/value/tenant/${tenantId}/roi`);
    }

    getAgentValue(tenantId: string, params: Params = {}): Promise<AgentValueResponse> {
        const query = buildQuery(params);
        return this.request<AgentValueResponse>(query ? `/api/value/tenant/${tenantId}/agents?${query}` : `/api/value/tenant/${tenantId}/agents`);
    }

    getEquityImpact(tenantId: string, params: Params = {}): Promise<EquityImpactResponse> {
        const query = buildQuery(params);
        return this.request<EquityImpactResponse>(query ? `/api/value/tenant/${tenantId}/equity?${query}` : `/api/value/tenant/${tenantId}/equity`);
    }

    getBaselines(tenantId: string): Promise<BaselinesResponse> {
        return this.request<BaselinesResponse>(`/api/value/tenant/${tenantId}/baselines`);
    }

    setBaseline(tenantId: string, payload: JsonBody): Promise<SetBaselineResponse> {
        return this.request<SetBaselineResponse>(`/api/value/tenant/${tenantId}/baseline`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    getCostConfig(tenantId: string): Promise<CostConfigResponse> {
        return this.request<CostConfigResponse>(`/api/value/tenant/${tenantId}/cost-config`);
    }

    setCostConfig(tenantId: string, payload: JsonBody): Promise<SetCostConfigResponse> {
        return this.request<SetCostConfigResponse>(`/api/value/tenant/${tenantId}/cost-config`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    getValueSummary(tenantId: string, startDate: string, endDate: string): Promise<ValueSummaryResponse> {
        return this.request<ValueSummaryResponse>(`/api/value/tenant/${tenantId}/summary?startDate=${startDate}&endDate=${endDate}`);
    }

    // ========================================================================
    // PLATFORM (MCP) ENDPOINTS
    // ========================================================================

    getMCPTools(): Promise<MCPToolsResponse> {
        return this.request<MCPToolsResponse>('/api/mcp/tools');
    }

    getMCPAgents(): Promise<MCPAgentsResponse> {
        return this.request<MCPAgentsResponse>('/api/mcp/agents');
    }

    healthCheck(): Promise<HealthCheckResponse> {
        return this.request<HealthCheckResponse>('/health');
    }

    // ========================================================================
    // INTERNAL ADMIN ENDPOINTS
    // ========================================================================

    getPlatformROI(params: Params = {}): Promise<PlatformROIResponse> {
        const query = buildQuery(params);
        return this.request<PlatformROIResponse>(query ? `/api/value/admin/roi?${query}` : '/api/value/admin/roi');
    }

    getPlatformValueByTenant(params: Params = {}): Promise<PlatformValueByTenantResponse> {
        const query = buildQuery(params);
        return this.request<PlatformValueByTenantResponse>(query ? `/api/value/admin/by-tenant?${query}` : '/api/value/admin/by-tenant');
    }

    getPlatformValueByAgent(params: Params = {}): Promise<PlatformValueByAgentResponse> {
        const query = buildQuery(params);
        return this.request<PlatformValueByAgentResponse>(query ? `/api/value/admin/by-agent?${query}` : '/api/value/admin/by-agent');
    }

    listAllTenants(params: Params = {}): Promise<AllTenantsResponse> {
        const query = buildQuery(params);
        return this.request<AllTenantsResponse>(query ? `/api/internal/tenants?${query}` : '/api/internal/tenants');
    }

    getTenantDetails(tenantId: string): Promise<TenantDetailsResponse> {
        return this.request<TenantDetailsResponse>(`/api/internal/tenants/${tenantId}`);
    }

    updateTenantAdmin(tenantId: string, data: JsonBody): Promise<UpdateTenantAdminResponse> {
        return this.request<UpdateTenantAdminResponse>(`/api/internal/tenants/${tenantId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    getPlatformRevenue(params: Params = {}): Promise<PlatformRevenueResponse> {
        const query = buildQuery(params);
        return this.request<PlatformRevenueResponse>(query ? `/api/internal/revenue?${query}` : '/api/internal/revenue');
    }

    listVouchers(params: Params = {}): Promise<VouchersListResponse> {
        const query = buildQuery(params);
        return this.request<VouchersListResponse>(query ? `/api/internal/vouchers?${query}` : '/api/internal/vouchers');
    }

    createVoucher(data: JsonBody): Promise<CreateVoucherResponse> {
        return this.request<CreateVoucherResponse>('/api/internal/vouchers', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    revokeVoucher(voucherId: string): Promise<RevokeVoucherResponse> {
        return this.request<RevokeVoucherResponse>(`/api/internal/vouchers/${voucherId}/revoke`, { method: 'POST' });
    }

    listSupportTickets(params: Params = {}): Promise<SupportTicketsListResponse> {
        const query = buildQuery(params);
        return this.request<SupportTicketsListResponse>(query ? `/api/internal/support?${query}` : '/api/internal/support');
    }

    updateSupportTicket(ticketId: string, data: JsonBody): Promise<UpdateSupportTicketResponse> {
        return this.request<UpdateSupportTicketResponse>(`/api/internal/support/${ticketId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    listAllVendors(params: Params = {}): Promise<AllVendorsResponse> {
        const query = buildQuery(params);
        return this.request<AllVendorsResponse>(query ? `/api/internal/vendors?${query}` : '/api/internal/vendors');
    }

    getSystemHealth(): Promise<SystemHealthResponse> {
        return this.request<SystemHealthResponse>('/api/internal/health');
    }

    listFeatureFlags(): Promise<FeatureFlagsResponse> {
        return this.request<FeatureFlagsResponse>('/api/internal/feature-flags');
    }

    updateFeatureFlag(flagId: string, data: JsonBody): Promise<UpdateFeatureFlagResponse> {
        return this.request<UpdateFeatureFlagResponse>(`/api/internal/feature-flags/${flagId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    getAdminAuditLogs(params: Params = {}): Promise<AdminAuditLogsResponse> {
        const query = buildQuery(params);
        return this.request<AdminAuditLogsResponse>(query ? `/api/internal/audit?${query}` : '/api/internal/audit');
    }

    // ========================================================================
    // STAFF USER MANAGEMENT (Platform Admins)
    // ========================================================================

    listStaffUsers(params: Params = {}): Promise<StaffUsersListResponse> {
        const query = buildQuery(params);
        return this.request<StaffUsersListResponse>(query ? `/api/staff/users?${query}` : '/api/staff/users');
    }

    createStaffUser(data: JsonBody): Promise<StaffUserResponse> {
        return this.request<StaffUserResponse>('/api/staff/users', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    updateStaffUser(userId: string, data: JsonBody): Promise<StaffUserResponse> {
        return this.request<StaffUserResponse>(`/api/staff/users/${userId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    deleteStaffUser(userId: string): Promise<DeleteStaffUserResponse> {
        return this.request<DeleteStaffUserResponse>(`/api/staff/users/${userId}`, {
            method: 'DELETE',
        });
    }

    // =========================================================================
    // DISTRICT LANGUAGES  (/api/i18n/languages, /api/i18n/admin/languages/*)
    // =========================================================================
    //
    // The frame for migrations 738/739. Before these, the only way to change a
    // district's language set was to issue PUT /api/i18n/admin/languages/:code
    // by hand — which is to say there was no way, for anyone who actually
    // administers a district.

    /** The languages THIS district offers, in the order families see them. */
    // =========================================================================
    // Benefit coverage (/api/admin/benefit-coverage) — migration 869
    // =========================================================================

    /**
     * Every benefits program with this district's standing for it.
     *
     * `state_known: false` means the district has no state_code, and then NO
     * program can be answered for — the screen must say that rather than render
     * an empty table, which reads as "all clear".
     */
    async getBenefitCoverage(): Promise<BenefitCoverageResponse> {
        return this.request<BenefitCoverageResponse>('/api/admin/benefit-coverage');
    }

    /** One program's verdict, for a surface that needs to ask before offering it. */
    async getProgramServable(programId: string): Promise<ProgramServableResponse> {
        return this.request<ProgramServableResponse>(
            `/api/admin/benefit-coverage/${encodeURIComponent(programId)}/servable`);
    }

    /**
     * Move a gap along open -> drafted -> checked -> live.
     *
     * The actor is NOT a parameter: the server takes it from the session, and the
     * database refuses `checked` and `live` without one. A 422 means the
     * lifecycle refused the transition, and its message is the procedure's own.
     */
    async advanceBenefitGap(programId: string, body: {
        state_code: string;
        to_status: GapStatus;
        drafted_by_agent?: boolean;
        reason?: string | null;
    }): Promise<AdvanceGapResult> {
        return this.request<AdvanceGapResult>(
            `/api/admin/benefit-coverage/${encodeURIComponent(programId)}/gap`,
            { method: 'POST', body: JSON.stringify(body) });
    }

    /** The CHECKED local rules in force for a program. Drafts are not returned. */
    async getLocalBenefitRules(programId: string): Promise<LocalBenefitRuleListResponse> {
        return this.request<LocalBenefitRuleListResponse>(
            `/api/admin/benefit-coverage/${encodeURIComponent(programId)}/local-rules`);
    }

    async getDistrictLanguages(): Promise<DistrictLanguageResponse[]> {
        return this.request<DistrictLanguageResponse[]>('/api/i18n/languages');
    }

    /**
     * The full platform catalogue, for choosing a language not yet enabled.
     *
     * Deliberately the same endpoint read WITHOUT a tenant: it answers the
     * district's set when one resolves and the global catalogue otherwise, so
     * there is no second list to drift.
     */
    async getLanguageCatalogue(search?: string): Promise<LanguageCatalogueEntry[]> {
        const q = search && search.trim() ? `&q=${encodeURIComponent(search.trim())}` : '';
        return this.request<LanguageCatalogueEntry[]>(`/api/i18n/languages?catalogue=1${q}`);
    }

    /**
     * What languages this district's own families speak, ranked (migration 812).
     *
     * This is what the Languages screen opens with. 642 catalogue entries are
     * the fallback for a language the district's data has not seen; its own
     * home-language counts are the answer for everything else.
     */
    async getHomeLanguageDemand(minStudents?: number): Promise<HomeLanguageDemandResponse> {
        const q = minStudents && minStudents > 1 ? `?minStudents=${minStudents}` : '';
        return this.request<HomeLanguageDemandResponse>(`/api/i18n/admin/languages/demand${q}`);
    }

    /**
     * What families said, and which languages THEY added.
     *
     * This is how the district admin is told a parent put a language in their
     * list — the ruling's "tell the district admin it was added and by how many
     * families".
     */
    async getFamilyLanguageCounts(): Promise<FamilyLanguageCountsResponse> {
        return this.request<FamilyLanguageCountsResponse>('/api/i18n/admin/languages/family-counts');
    }

    /** Pre-built sets a district picks at setup, so day one is not empty. */
    async getStartingSets(): Promise<StartingSetsResponse> {
        return this.request<StartingSetsResponse>('/api/i18n/admin/languages/starting-sets');
    }

    /** Apply one. Additive — it never removes a language already offered. */
    async applyStartingSet(setKey: string): Promise<ApplyStartingSetResponse> {
        return this.request<ApplyStartingSetResponse>(
            `/api/i18n/admin/languages/starting-sets/${encodeURIComponent(setKey)}/apply`,
            { method: 'POST', body: JSON.stringify({}) },
        );
    }

    /**
     * Match the EL office's own list against the catalogue.
     *
     * Read-only: it reports, it does not enable. The admin decides what to do
     * with the matches, and sees every unmatched line rather than a count that
     * quietly excluded them.
     */
    async matchLanguageNames(names: string): Promise<LanguageMatchResponse> {
        return this.request<LanguageMatchResponse>('/api/i18n/admin/languages/match',
            { method: 'POST', body: JSON.stringify({ names }) });
    }

    /** Turn a language on or off for this district, and set where it sorts. */
    async setDistrictLanguage(
        languageCode: string,
        body: { isEnabled: boolean; sortOrder?: number },
    ): Promise<SetDistrictLanguageResponse> {
        return this.request<SetDistrictLanguageResponse>(
            `/api/i18n/admin/languages/${encodeURIComponent(languageCode)}`,
            { method: 'PUT', body: JSON.stringify(body) },
        );
    }

    /** How far each language's translation got. */
    async getDistrictLanguageFills(): Promise<DistrictLanguageFillListResponse> {
        return this.request<DistrictLanguageFillListResponse>('/api/i18n/admin/languages/fills');
    }

    /**
     * Run ONE bounded fill pass. Returns `remaining`; the caller re-invokes
     * until it is 0. Bounded because 4,126 keys is far more model time than one
     * HTTP request should hold open, and every pass is resumable.
     */
    async fillDistrictLanguage(
        languageCode: string,
        maxKeys?: number,
    ): Promise<DistrictLanguageFillPassResponse> {
        return this.request<DistrictLanguageFillPassResponse>(
            `/api/i18n/admin/languages/${encodeURIComponent(languageCode)}/fill`,
            { method: 'POST', body: JSON.stringify(maxKeys ? { maxKeys } : {}) },
        );
    }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

export const apiClient: APIClient = new APIClient();
export default apiClient;
